import type { FocusEvent, KeyboardEvent, PointerEvent } from "react";

/**
 * Pressed-state tracking without React state.
 *
 * `pressHandlers` returns the seven event handlers a control needs to mirror
 * its press state into a `data-pressed` attribute. The attribute is written
 * straight to the DOM, so a press never re-renders the control or its
 * children; the stylesheet (`pressable.module.css`) owns the visual.
 *
 * Pointer: down sets, up / cancel / leave clear. Keyboard: Space keydown sets,
 * keyup clears (Enter fires on keydown like a native button, so it carries no
 * pressed frame). Blur always clears so a control cannot stay stuck down.
 *
 * Caller-supplied handlers run first and are never suppressed.
 */

export interface PressHandlerProps<E extends HTMLElement> {
    onPointerDown?: ((event: PointerEvent<E>) => void) | undefined;
    onPointerUp?: ((event: PointerEvent<E>) => void) | undefined;
    onPointerCancel?: ((event: PointerEvent<E>) => void) | undefined;
    onPointerLeave?: ((event: PointerEvent<E>) => void) | undefined;
    onKeyDown?: ((event: KeyboardEvent<E>) => void) | undefined;
    onKeyUp?: ((event: KeyboardEvent<E>) => void) | undefined;
    onBlur?: ((event: FocusEvent<E>) => void) | undefined;
}

export function setPressed(element: HTMLElement, pressed: boolean): void {
    if (pressed) {
        element.setAttribute("data-pressed", "");
    } else {
        element.removeAttribute("data-pressed");
    }
}

/** Pointer capture with a guard for environments that lack it (jsdom). */
export function capturePointer(element: HTMLElement, pointerId: number): void {
    if (typeof element.setPointerCapture === "function") {
        element.setPointerCapture(pointerId);
    }
}

export function releasePointer(element: HTMLElement, pointerId: number): void {
    if (
        typeof element.hasPointerCapture === "function" &&
        element.hasPointerCapture(pointerId)
    ) {
        element.releasePointerCapture(pointerId);
    }
}

export function pressHandlers<E extends HTMLElement>(
    user: PressHandlerProps<E>,
    inert: boolean,
): Required<PressHandlerProps<E>> {
    return {
        onPointerDown: (event) => {
            user.onPointerDown?.(event);
            if (!inert && event.button === 0) {
                setPressed(event.currentTarget, true);
            }
        },
        onPointerUp: (event) => {
            user.onPointerUp?.(event);
            setPressed(event.currentTarget, false);
        },
        onPointerCancel: (event) => {
            user.onPointerCancel?.(event);
            setPressed(event.currentTarget, false);
        },
        onPointerLeave: (event) => {
            user.onPointerLeave?.(event);
            setPressed(event.currentTarget, false);
        },
        onKeyDown: (event) => {
            user.onKeyDown?.(event);
            if (!inert && event.key === " " && !event.repeat) {
                setPressed(event.currentTarget, true);
            }
        },
        onKeyUp: (event) => {
            user.onKeyUp?.(event);
            if (event.key === " ") {
                setPressed(event.currentTarget, false);
            }
        },
        onBlur: (event) => {
            user.onBlur?.(event);
            setPressed(event.currentTarget, false);
        },
    };
}
